import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useSearchParams, useParams } from 'react-router-dom';
import axios from 'axios';
import { Hammer, Pencil, Search, X, ArrowLeft, Check, RefreshCw, Play } from 'lucide-react';
import { useEditDocument, EditabilityBanner, saveErrorMessage } from '../nativerecords/documentEdit';
import './NativeWorkOrderNew.css';

// New Work Order (Native) — manual WO entry + demand generation on the native
// platform (task #43). Replaces hand-typing WOs into NetSuite.
//
// Panel 1 (manual): assembly picker (assemblies only), planned qty, location,
// make-to-order header context (customer, order ref, ship date, special
// instructions) and a live BOM preview with per-component availability —
// shortages highlighted. Creates a DRAFT via packages/production
// (generation_mode 'manual', BOM snapshotted at creation, idempotent on a
// client-generated MAN-WO-… ref).
//
// Panel 2 (Generate from demand): generateMakeToOrder (policy-matched orders,
// one WO per SO line, no netting) and generateFromSalesOrders (stock mode,
// netted) — each runs a DRY-RUN preview first, then a confirm that creates
// the drafts and lists them.
//
// Native-first: no NetSuite write from this screen; the NS copy arrives with
// the production reverse-sync worker (not built yet).
//
// EDIT MODE: mounted at /native-work-orders/:id/edit the same screen loads that
// work order, prefills itself, and saves with PATCH. A WO posts nothing until
// components are issued, so a draft is freely editable — but the ASSEMBLY never
// is (its BOM was snapshotted at creation), and once the WO is released or
// anything has been issued or built the planned quantity freezes too. The
// server owns those rules (nativeDocEdit.js); its verdict arrives as
// `editability`. The Generate-from-demand panel is hidden in edit mode — it
// creates new work orders, it does not amend this one.

const PRIORITIES = [
  { key: 'Normal', label: 'Normal', hint: 'Standard scheduling on the production board.' },
  { key: 'High', label: 'High', hint: 'Schedule ahead of Normal builds.' },
  { key: 'Rush', label: 'Rush', hint: 'Front of the line — use sparingly.' },
];

const newRef = () => `MAN-WO-${Date.now()}-${Math.floor(Math.random() * 1e6)}`;
const qty3 = (n) => (Number(n) % 1 === 0 ? String(Number(n)) : Number(n).toFixed(3));

function useDebouncedSearch(fetcher, minLen = 2) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  useEffect(() => {
    if (query.trim().length < minLen) { setResults([]); return undefined; }
    const t = setTimeout(async () => {
      try {
        setSearching(true);
        setResults(await fetcher(query.trim()));
      } catch {
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [query, fetcher, minLen]);
  return { query, setQuery, results, setResults, searching };
}

// Assembly search reuses the native-items endpoint, filtered to assemblies.
const searchAssemblies = async (q) => {
  const { data } = await axios.get('/api/native-items', { params: { q, type: 'Assembly', pageSize: 15 } });
  return data.items || [];
};

function AvailabilityTable({ components }) {
  if (!components || components.length === 0) return null;
  return (
    <table className="nwo-table">
      <thead>
        <tr><th>SKU</th><th>Component</th><th className="r">Required</th><th className="r">Available</th><th className="r">Shortfall</th></tr>
      </thead>
      <tbody>
        {components.map((c, i) => (
          <tr key={i} className={c.uomMismatch ? 'nwo-row-warn' : (c.sufficient ? '' : 'nwo-row-short')}>
            <td><code>{c.componentSku}</code></td>
            <td>
              {c.componentName || c.componentSku}
              {c.uomMismatch && <div className="nwo-uom-note">consumption UOM differs from stock UOM — quantities not comparable (no converter yet)</div>}
            </td>
            <td className="r">{qty3(c.required)}{c.unit ? ` ${c.unit}` : ''}</td>
            <td className="r">{qty3(c.available)}</td>
            <td className="r">{c.uomMismatch ? '?' : (c.sufficient ? '—' : <strong>{qty3(c.shortfall)}</strong>)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export default function NativeWorkOrderNew() {
  const { id: editId } = useParams();
  const isEdit = Boolean(editId);
  const [step, setStep] = useState('edit'); // edit | review | done
  const [priority, setPriority] = useState('Normal');
  const [assembly, setAssembly] = useState(null);
  const [quantity, setQuantity] = useState('');
  const [locationId, setLocationId] = useState('');
  const [locations, setLocations] = useState([]);
  const [shipDate, setShipDate] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [orderRef, setOrderRef] = useState('');
  const [instructions, setInstructions] = useState('');
  const [memo, setMemo] = useState('');
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [confirmation, setConfirmation] = useState(null);
  const externalRef = useRef(newRef());

  const assemblySearch = useDebouncedSearch(searchAssemblies);

  // ── edit mode ──────────────────────────────────────────────────────────────
  const [editability, setEditability] = useState(null);
  const [woNumber, setWoNumber] = useState(null);
  const [woStatus, setWoStatus] = useState(null);
  const [releasing, setReleasing] = useState(false);
  const expectedUpdatedAt = useRef(undefined);
  const loaded = useEditDocument(isEdit ? `/api/native-production/${encodeURIComponent(editId)}` : null, (d) => d.workOrder);

  useEffect(() => {
    const wo = loaded.doc;
    if (!wo) return;
    expectedUpdatedAt.current = wo.updated_at ?? null;
    externalRef.current = wo.idempotency_key || externalRef.current;
    setEditability(loaded.editability);
    setWoNumber(wo.wo_number);
    setWoStatus(wo.status);
    setAssembly({ id: wo.assembly_item_id, sku: wo.assembly_sku, name: wo.assembly_sku });
    setQuantity(String(Number(wo.quantity)));
    setLocationId(wo.location_id || '');
    setPriority(wo.priority || 'Normal');
    setShipDate(wo.ship_date ? String(wo.ship_date).slice(0, 10) : '');
    setCustomerName(wo.customer_name || '');
    setOrderRef(wo.order_ref || '');
    setInstructions(wo.special_instructions || '');
    setMemo(wo.memo || '');
    // Fill the assembly's display name from the item master (the WO header
    // only denormalizes the SKU).
    if (wo.assembly_item_id) {
      axios.get(`/api/native-items/${encodeURIComponent(wo.assembly_item_id)}`)
        .then(({ data }) => { if (data.item) setAssembly({ id: data.item.id, sku: data.item.sku, name: data.item.name }); })
        .catch(() => { /* the SKU alone is enough to work with */ });
    }
  }, [loaded.doc, loaded.editability]);

  const locked = isEdit && editability != null && !editability.editable;
  const restricted = isEdit && editability != null && editability.mode === 'restricted';

  // Make Copy (?copyFrom=<work-order record id>): prefill assembly, quantity,
  // and make-to-order context from the mirrored record.
  const [searchParams] = useSearchParams();
  const [copyNote, setCopyNote] = useState(null);
  useEffect(() => {
    const copyFrom = searchParams.get('copyFrom');
    if (!copyFrom || isEdit) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`/api/native-records/work-orders/${encodeURIComponent(copyFrom)}`);
        if (cancelled) return;
        const rec = data.record || {};
        const sku = rec.item_sku || data.mirror?.item_sku;
        if (sku) {
          try {
            const { data: it } = await axios.get(`/api/native-items/${encodeURIComponent(sku)}`);
            if (it.item && !cancelled) setAssembly({ id: it.item.id, sku: it.item.sku, name: it.item.name });
          } catch { /* assembly stays unset — operator picks */ }
        }
        if (cancelled) return;
        if (Number(rec.quantity) > 0) setQuantity(String(Number(rec.quantity)));
        if (rec.customer_name || rec.customer) setCustomerName(rec.customer_name || rec.customer);
        setMemo(`Copy of ${rec.work_order_number || rec.tranid || copyFrom}`);
        setCopyNote(`Copied from ${rec.work_order_number || rec.tranid || 'the source work order'} — the BOM snapshots fresh at creation; review quantity and context before posting.`);
      } catch (err) {
        if (!cancelled) setCopyNote(`Could not copy from the source work order: ${err.response?.data?.error || err.message}`);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Generate-from-demand panel state (per mode: mto | stock).
  const [gen, setGen] = useState({ mto: null, stock: null });
  const [genBusy, setGenBusy] = useState(null); // 'mto-preview' | 'mto-confirm' | 'stock-preview' | 'stock-confirm'
  const [genError, setGenError] = useState(null);

  useEffect(() => {
    axios.get('/api/native-production/locations')
      .then(({ data }) => setLocations(data.locations || []))
      .catch(() => setLocations([]));
  }, []);

  // Live BOM + availability preview (debounced on assembly/qty/location).
  const loadPreview = useCallback(async () => {
    if (!assembly || !(Number(quantity) > 0)) { setPreview(null); return; }
    try {
      setPreviewLoading(true);
      const { data } = await axios.get('/api/native-production/bom-preview', {
        params: { assembly: assembly.id, quantity: Number(quantity), locationId: locationId || undefined },
      });
      setPreview(data);
    } catch (err) {
      setPreview({ error: err.response?.data?.error || err.message });
    } finally {
      setPreviewLoading(false);
    }
  }, [assembly, quantity, locationId]);

  useEffect(() => {
    const t = setTimeout(loadPreview, 400);
    return () => clearTimeout(t);
  }, [loadPreview]);

  const shortages = useMemo(
    () => (preview?.components || []).filter((c) => !c.sufficient),
    [preview]
  );

  const validate = () => {
    if (!assembly) return 'Pick an assembly item first.';
    if (!(Number(quantity) > 0)) return 'Planned quantity must be greater than 0.';
    return null;
  };

  const goReview = () => {
    const problem = validate();
    if (problem) { setError(problem); return; }
    setError(null);
    setStep('review');
  };

  const submit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const body = {
        externalRef: externalRef.current,
        assemblyItemId: assembly.id,
        quantity: Number(quantity),
        locationId: locationId || null,
        priority,
        shipDate: shipDate || null,
        customerName: customerName.trim() || null,
        orderRef: orderRef.trim() || null,
        specialInstructions: instructions.trim() || null,
        memo: memo.trim() || null,
      };
      const { data } = isEdit
        ? await axios.patch(`/api/native-production/${encodeURIComponent(editId)}`, { ...body, expectedUpdatedAt: expectedUpdatedAt.current })
        : await axios.post('/api/native-production', body);
      if (isEdit) {
        // Re-arm the compare-and-swap so a second save from this screen works.
        expectedUpdatedAt.current = data.workOrder?.updated_at ?? null;
        setEditability(data.editability || editability);
      }
      setConfirmation(data);
      setStep('done');
    } catch (err) {
      setError(saveErrorMessage(err));
      setStep('edit');
    } finally {
      setSubmitting(false);
    }
  };

  // Release a draft to the floor. The server transitions the status and
  // pushes the WO into the work-order cache bridge, so it appears on the
  // batching/blending screens immediately.
  const releaseWo = async (woId) => {
    setReleasing(true);
    setError(null);
    try {
      const { data } = await axios.post(`/api/native-production/${encodeURIComponent(woId)}/release`);
      setWoStatus('released');
      setConfirmation((prev) => (prev ? {
        ...prev,
        workOrder: data.workOrder || { ...prev.workOrder, status: 'released' },
        netsuiteNote: 'Released to the floor — this work order is now available on the batching screen.',
      } : prev));
      if (isEdit) expectedUpdatedAt.current = data.workOrder?.updated_at ?? expectedUpdatedAt.current;
      return true;
    } catch (err) {
      setError(err.response?.data?.error || err.message);
      return false;
    } finally {
      setReleasing(false);
    }
  };

  const reset = () => {
    externalRef.current = newRef();
    setStep('edit');
    setPriority('Normal');
    setAssembly(null);
    setQuantity('');
    setLocationId('');
    setShipDate('');
    setCustomerName('');
    setOrderRef('');
    setInstructions('');
    setMemo('');
    setPreview(null);
    setConfirmation(null);
    setError(null);
  };

  // ── generate-from-demand actions ───────────────────────────────────────────
  const runGeneration = async (mode, dryRun) => {
    const url = mode === 'mto' ? '/api/native-production/generate/make-to-order' : '/api/native-production/generate/stock';
    setGenBusy(`${mode}-${dryRun ? 'preview' : 'confirm'}`);
    setGenError(null);
    try {
      const { data } = await axios.post(url, { dryRun });
      setGen((prev) => ({ ...prev, [mode]: data }));
    } catch (err) {
      setGenError(err.response?.data?.error || err.message);
    } finally {
      setGenBusy(null);
    }
  };

  const genPanel = (mode, title, hint) => {
    const r = gen[mode];
    const planned = r?.dryRun ? (r.planned || []) : null;
    const created = r && !r.dryRun ? (r.created || []) : null;
    const absorbed = r && !r.dryRun ? (r.absorbed || []) : null;
    return (
      <div className="nwo-gen-block">
        <div className="nwo-gen-head">
          <div>
            <strong>{title}</strong>
            <span className="nwo-gen-hint">{hint}</span>
          </div>
          <div className="nwo-gen-actions">
            <button
              className="nwo-btn"
              disabled={genBusy != null}
              onClick={() => runGeneration(mode, true)}
            >
              <RefreshCw size={14} /> {genBusy === `${mode}-preview` ? 'Previewing…' : 'Preview (dry-run)'}
            </button>
            {planned && planned.length > 0 && (
              <button
                className="nwo-btn nwo-btn-primary"
                disabled={genBusy != null}
                onClick={() => runGeneration(mode, false)}
              >
                <Play size={14} /> {genBusy === `${mode}-confirm` ? 'Creating…' : `Create ${planned.length} draft${planned.length === 1 ? '' : 's'}`}
              </button>
            )}
          </div>
        </div>

        {planned && (
          planned.length === 0 ? (
            <p className="nwo-hint">Nothing to generate — every matched demand line is already pegged or covered by supply.</p>
          ) : (
            <table className="nwo-table">
              <thead>
                <tr>
                  <th>Assembly</th><th className="r">Qty</th>
                  {mode === 'mto' ? <><th>Customer</th><th>Order</th><th>Ship</th></> : <><th>Action</th><th className="r">Demand</th><th className="r">On hand</th><th className="r">Open WOs</th></>}
                </tr>
              </thead>
              <tbody>
                {planned.map((p, i) => (
                  <tr key={i}>
                    <td><code>{p.assemblySku}</code> {p.assemblyName}</td>
                    <td className="r">{qty3(p.quantity)}</td>
                    {mode === 'mto' ? (
                      <>
                        <td>{p.customerName || '—'}</td>
                        <td>{p.orderRef || '—'}</td>
                        <td>{p.shipDate || '—'}</td>
                      </>
                    ) : (
                      <>
                        <td>{p.action === 'absorb' ? `absorb into ${p.absorbIntoWoNumber || 'existing draft'}` : 'new draft'}</td>
                        <td className="r">{qty3(p.demand)}</td>
                        <td className="r">{qty3(p.onHand)}</td>
                        <td className="r">{qty3(p.openSupply)}</td>
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          )
        )}

        {created && (
          <div className="nwo-gen-result">
            {created.length === 0 && (!absorbed || absorbed.length === 0) ? (
              <p className="nwo-hint">No drafts created — demand was already pegged or covered.</p>
            ) : (
              <>
                <p className="nwo-gen-created"><Check size={14} /> Created {created.length} draft work order{created.length === 1 ? '' : 's'}{absorbed && absorbed.length > 0 ? `, absorbed demand into ${absorbed.length} existing draft${absorbed.length === 1 ? '' : 's'}` : ''}:</p>
                <ul className="nwo-gen-list">
                  {created.map((c, i) => (
                    <li key={i}>
                      <code>{c.workOrder.wo_number}</code> — {c.workOrder.assembly_sku} × {qty3(c.workOrder.quantity)}
                      {c.workOrder.customer_name ? ` for ${c.workOrder.customer_name}` : ''} <span className="nwo-tag">draft</span>
                    </li>
                  ))}
                  {(absorbed || []).map((a, i) => (
                    <li key={`a${i}`}>
                      <code>{a.workOrder.wo_number}</code> — quantity raised to {qty3(a.workOrder.quantity)} (absorbed +{qty3(a.addedQuantity)}) <span className="nwo-tag">draft</span>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </div>
        )}
      </div>
    );
  };

  // ── confirmation ───────────────────────────────────────────────────────────
  if (step === 'done' && confirmation) {
    const wo = confirmation.workOrder;
    const avail = confirmation.availability;
    const short = (avail?.components || []).filter((c) => !c.sufficient);
    return (
      <div className="nwo-page">
        <div className="nwo-card nwo-confirm">
          <div className="nwo-confirm-icon"><Check size={28} /></div>
          <h2>{isEdit ? 'Work order updated' : 'Work order created'}</h2>
          <div className="nwo-confirm-grid">
            <span>WO number</span><code>{wo.wo_number}</code>
            <span>Assembly</span><strong>{wo.assembly_sku}</strong>
            <span>Planned qty</span><strong>{qty3(wo.quantity)}</strong>
            <span>Status</span><strong>{wo.status}</strong>
            {wo.customer_name && <><span>Customer</span><strong>{wo.customer_name}</strong></>}
            {wo.ship_date && <><span>Ship date</span><strong>{wo.ship_date}</strong></>}
          </div>
          {avail && (
            short.length === 0 ? (
              <p className="nwo-avail-ok">All components available for this build.</p>
            ) : (
              <div className="nwo-avail-warn">
                <p>{short.length} component{short.length === 1 ? ' is' : 's are'} short for this build:</p>
                <AvailabilityTable components={short} />
              </div>
            )
          )}
          <p className="nwo-note">{confirmation.netsuiteNote}</p>
          {error && <div className="nwo-error">{error}</div>}
          <div className="nwo-actions">
            {wo.status === 'draft' && (
              <button
                className="nwo-btn nwo-btn-primary"
                disabled={releasing}
                onClick={() => releaseWo(wo.id)}
              >
                <Play size={16} /> {releasing ? 'Releasing…' : 'Release to floor'}
              </button>
            )}
            {['released', 'in_progress'].includes(wo.status) && (
              <a className="nwo-btn nwo-btn-primary" href={`/native-assembly-builds/new?workOrderId=${encodeURIComponent(wo.id)}`}>
                <Hammer size={16} /> Post build
              </a>
            )}
            {isEdit ? (
              <button className="nwo-btn nwo-btn-ghost" onClick={() => setStep('edit')}>Back to the work order</button>
            ) : (
              <button className="nwo-btn nwo-btn-ghost" onClick={reset}>Create another work order</button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ── review ─────────────────────────────────────────────────────────────────
  if (step === 'review') {
    return (
      <div className="nwo-page">
        <div className="nwo-header">
          <button className="nwo-btn nwo-btn-ghost" onClick={() => setStep('edit')}><ArrowLeft size={16} /> Back to edit</button>
          <h1>{isEdit ? `Review changes to ${woNumber || 'this work order'}` : 'Review work order'}</h1>
        </div>
        {error && <div className="nwo-error">{error}</div>}
        <div className="nwo-card">
          <div className="nwo-review-row"><span>Assembly</span><code>{assembly?.sku}</code><strong>{assembly?.name}</strong></div>
          <div className="nwo-review-row"><span>Planned qty</span><strong>{quantity}</strong></div>
          <div className="nwo-review-row"><span>Priority</span><strong>{priority}</strong></div>
          <div className="nwo-review-row"><span>Location</span><strong>{locations.find((l) => l.id === locationId)?.name || 'None (item default)'}</strong></div>
          {shipDate && <div className="nwo-review-row"><span>Ship date</span><strong>{shipDate}</strong></div>}
          {customerName && <div className="nwo-review-row"><span>Customer</span><strong>{customerName}</strong></div>}
          {orderRef && <div className="nwo-review-row"><span>Order ref</span><strong>{orderRef}</strong></div>}
          {instructions && <div className="nwo-review-row"><span>Instructions</span><em>{instructions}</em></div>}
          {memo && <div className="nwo-review-row"><span>Memo</span><em>{memo}</em></div>}

          {preview && !preview.error && (
            <>
              <h3 className="nwo-review-bom">BOM snapshot preview</h3>
              {shortages.length > 0 && (
                <div className="nwo-error">
                  {shortages.length} component{shortages.length === 1 ? ' is' : 's are'} short — the draft is still created (release is a separate, human step).
                </div>
              )}
              <AvailabilityTable components={preview.components} />
            </>
          )}
          <p className="nwo-hint">Creates a native DRAFT work order — it does not hit the production floor until released, and nothing is written to NetSuite from this screen.</p>
          <div className="nwo-actions">
            <button className="nwo-btn nwo-btn-ghost" onClick={() => setStep('edit')}>Back</button>
            <button className="nwo-btn nwo-btn-primary" disabled={submitting || locked} onClick={submit}>
              {submitting ? 'Saving…' : (isEdit ? 'Save changes' : 'Create work order')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── edit ───────────────────────────────────────────────────────────────────
  return (
    <div className="nwo-page">
      <div className="nwo-header">
        {isEdit ? (
          <>
            <h1><Pencil size={22} /> Edit Work Order {woNumber || ''}</h1>
            <p className="nwo-sub">Nothing has posted against this work order, so changes save in place. The assembly is fixed — its BOM was snapshotted when the WO was created.</p>
            {woStatus === 'draft' && (
              <button
                className="nwo-btn nwo-btn-primary"
                disabled={releasing}
                onClick={async () => {
                  const ok = await releaseWo(editId);
                  if (ok) setCopyNote('Released to the floor — this work order is now available on the batching screen. The planned quantity is frozen from here.');
                }}
              >
                <Play size={16} /> {releasing ? 'Releasing…' : 'Release to floor'}
              </button>
            )}
            {woStatus && woStatus !== 'draft' && <span className="nwo-tag">{woStatus}</span>}
            {['released', 'in_progress'].includes(woStatus) && (
              <a className="nwo-btn nwo-btn-primary" href={`/native-assembly-builds/new?workOrderId=${encodeURIComponent(editId)}`}>
                <Hammer size={16} /> Post build
              </a>
            )}
          </>
        ) : (
          <>
            <h1><Hammer size={22} /> New Work Order (Native)</h1>
            <p className="nwo-sub">Manual work order on the native platform — created as a draft with a BOM snapshot. No NetSuite typing.</p>
          </>
        )}
      </div>
      {loaded.loading && <div className="nwo-card"><p className="nwo-sub" style={{ margin: 0 }}>Loading the work order…</p></div>}
      {loaded.error && <div className="nwo-error">{loaded.error}</div>}
      <EditabilityBanner editability={editability} prefix="nwo" />
      {copyNote && <div className="nwo-card"><p className="nwo-sub" style={{ margin: 0 }}>{copyNote}</p></div>}
      {error && <div className="nwo-error">{error}</div>}

      <div className="nwo-card">
        <h3>Priority</h3>
        <div className="nwo-presets">
          {PRIORITIES.map((p) => (
            <button
              key={p.key}
              className={`nwo-preset ${priority === p.key ? 'active' : ''}`}
              disabled={locked}
              onClick={() => setPriority(p.key)}
            >
              <strong>{p.label}</strong>
              <span>{p.hint}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="nwo-card">
        <h3>Assembly</h3>
        {assembly ? (
          <div className="nwo-chip">
            <div>
              <code>{assembly.sku}</code>
              <strong>{assembly.name}</strong>
              {assembly.onHand != null && <em>{qty3(assembly.onHand)} on hand</em>}
            </div>
            {!isEdit && (
              <button className="nwo-icon-btn" onClick={() => { setAssembly(null); setPreview(null); }} title="Change assembly"><X size={16} /></button>
            )}
          </div>
        ) : (
          <div className="nwo-search">
            <Search size={16} className="nwo-search-icon" />
            <input
              className="nwo-input nwo-input-search"
              placeholder="Search assemblies by SKU or name…"
              value={assemblySearch.query}
              onChange={(e) => assemblySearch.setQuery(e.target.value)}
            />
            {(assemblySearch.results.length > 0 || assemblySearch.searching) && (
              <div className="nwo-results">
                {assemblySearch.searching && <div className="nwo-result-muted">Searching…</div>}
                {assemblySearch.results.map((it) => (
                  <button key={it.id} className="nwo-result" onClick={() => { setAssembly(it); assemblySearch.setQuery(''); assemblySearch.setResults([]); }}>
                    <code>{it.sku}</code>
                    <strong>{it.name}</strong>
                    {it.onHand != null && <em>{qty3(it.onHand)} on hand</em>}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        <div className="nwo-form-grid">
          <label>
            Planned quantity
            <input type="number" min="1" step="1" className="nwo-input" placeholder="0" value={quantity} disabled={locked || restricted} onChange={(e) => setQuantity(e.target.value)} />
          </label>
          <label>
            Location
            <select className="nwo-input" value={locationId} disabled={locked || restricted} onChange={(e) => setLocationId(e.target.value)}>
              <option value="">None (item default)</option>
              {locations.map((l) => <option key={l.id} value={l.id}>{l.name}{l.code ? ` (${l.code})` : ''}</option>)}
            </select>
          </label>
        </div>
      </div>

      <div className="nwo-card">
        <h3>Order context and notes</h3>
        <div className="nwo-form-grid">
          <label>
            Ship date
            <input type="date" className="nwo-input" value={shipDate} disabled={locked} onChange={(e) => setShipDate(e.target.value)} />
          </label>
          <label>
            Customer (optional)
            <input className="nwo-input" placeholder="Whose build is this?" value={customerName} disabled={locked} onChange={(e) => setCustomerName(e.target.value)} />
          </label>
          <label>
            Order ref (optional)
            <input className="nwo-input" placeholder="SO / PO reference" value={orderRef} disabled={locked} onChange={(e) => setOrderRef(e.target.value)} />
          </label>
        </div>
        <label className="nwo-label-block">
          Special instructions
          <textarea className="nwo-input" rows={2} placeholder="Build notes for the floor — stickers, labels, packing…" value={instructions} disabled={locked} onChange={(e) => setInstructions(e.target.value)} />
        </label>
        <label className="nwo-label-block">
          Memo
          <textarea className="nwo-input" rows={2} placeholder="Optional internal note" value={memo} disabled={locked} onChange={(e) => setMemo(e.target.value)} />
        </label>
      </div>

      <div className="nwo-card">
        <h3>BOM preview and component availability</h3>
        {!assembly || !(Number(quantity) > 0) ? (
          <p className="nwo-hint">Pick an assembly and enter a quantity to preview the BOM and check component availability.</p>
        ) : previewLoading ? (
          <p className="nwo-hint">Checking availability…</p>
        ) : preview?.error ? (
          <div className="nwo-error">{preview.error}</div>
        ) : preview ? (
          <>
            {preview.note && <div className="nwo-error">{preview.note}</div>}
            {preview.components.length > 0 && (
              shortages.length === 0 ? (
                <p className="nwo-avail-ok">All {preview.components.length} components available{locationId ? ' at this location' : ''}.</p>
              ) : (
                <p className="nwo-avail-short">{shortages.length} of {preview.components.length} components short — highlighted below.</p>
              )
            )}
            <AvailabilityTable components={preview.components} />
          </>
        ) : null}
      </div>

      <div className="nwo-footer">
        <div className="nwo-footer-totals">
          {assembly && Number(quantity) > 0
            ? <span>Build <strong>{qty3(quantity)}</strong> × <code>{assembly.sku}</code></span>
            : <span>Pick an assembly and quantity</span>}
        </div>
        <button className="nwo-btn nwo-btn-primary" onClick={goReview} disabled={!assembly || !(Number(quantity) > 0) || locked}>
          {isEdit ? 'Review changes' : 'Review work order'}
        </button>
      </div>

      {/* Generation creates NEW work orders — it has nothing to do with
          amending this one, so it is hidden in edit mode. */}
      {!isEdit && (
      <div className="nwo-card nwo-gen-card">
        <h3>Generate from demand</h3>
        <p className="nwo-hint nwo-gen-intro">
          Instead of typing WOs one at a time, generate drafts from open native sales orders.
          Both modes preview first (dry-run, writes nothing) and only create drafts on confirm —
          released to the floor separately.
        </p>
        {genError && <div className="nwo-error">{genError}</div>}
        {genPanel('mto', 'Make-to-order', 'Policy-matched orders (e.g. B&N program builds): one draft WO per order line at the full quantity — never netted against stock.')}
        {genPanel('stock', 'Stock (netted)', 'Assembly demand across open orders, netted against on-hand and open WO supply — one draft per assembly.')}
      </div>
      )}
    </div>
  );
}
